import {REG} from "/@lib@/commons/registry.js";
import {Action} from "/@lib@/commons/actions.js";
import {BoxContainer} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {EWedletEditMode, isEditableWedlet, IWedlet, IWedletActionCtx, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {IXmlIdMgr} from "/@lib@/edit/schema/xmlIdMgr.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {Button, OButtonInit} from "/@back@/commons/widgets/buttons.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IJmlNode, IJmlSubSet} from "/@lib@/commons/xml/jml.js";
import {IXmlMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {MsgLabel, OMsgLabelInit} from "/@back@/commons/basis.js";
import "/@back@/edit/wed/wedlets/wedlets_Perms.js";
import {AgEltBoxSelection} from "/@back@/edit/wed/features/boxSel.js";

// Base d'un widget pour gérer un xml:id.
export abstract class BoxId extends BoxContainer {

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		super.configWedletElt(tpl, wedlet);
		const sh = this.shadowRoot || this.attachShadow(DOMSH.SHADOWDOM_INIT); //On force la création du shdaow, indispensable pour le collaps.
		WEDLET.installSkins(tpl, sh, wedlet, this.localName);
	}

	fragmentId: string;

	refreshBindValue(val: IJmlNode, children?: IJmlSubSet) {
		super.refreshBindValue(val, children);
		this.fragmentId = val ? val.toString() : null;
		this.onFragmentIdChanged();
	}

	replaceChars(chars: string, msg: IXmlMsg) {
		super.replaceChars(chars, msg);
		this.fragmentId = chars;
		this.onFragmentIdChanged();
	}

	protected onFragmentIdChanged() {}
}

// AgEltBoxSelection(BoxId, {selMode: 'box', actionsLists: ['actions:wed:box-id', 'actions:wed:box'], groupOrder: '* edit'});
// window.customElements.define("box-id", BoxId);

/**
 * Action de génération d'un nouvel Id via l'aspect XmlIdMgr du docHolder.
 */
REG.reg.registerSvc("wedBoxIdChangeAction", 1, new Action<IWedletActionCtx>("changeId")
	.setLabel("Changer d'identifiant")
	.setGroup("edit")
	.setIcon("/@skin@/edit/wed/box/changeId.svg")
	.setVisible((ctx: IWedletActionCtx) => !ctx.wedlet.isVirtual() && isEditableWedlet(ctx.wedlet) && ctx.wedlet.editMode === EWedletEditMode.write)
	.setExecute(async (ctx: IWedletActionCtx) => {
		if (await POPUP.confirm("Attention : Changer d'identifiant aura pour effet de casser les liens établis vers ce fragment.",
			ctx.focusedElt, {okLbl: "Changer d'identifiant", kind: "warning"})) {
			const docHolder = ctx.wedlet.wedMgr.docHolder;
			const xa = ctx.wedlet.wedAnchor;
			const mgrId = docHolder.getAspect<IXmlIdMgr>(xa, null, "XmlIdMgr");
			if (mgrId) docHolder.newBatch({start: xa}).setAttr(xa, mgrId.generateId(new Set())).doBatch();
		}
	})
);
REG.reg.addSvcToList("actions:wed:box-id", "changeId", 1, 'wedBoxIdChangeAction');

/**
 * Action d'édition directe du xml:id.
 */
REG.reg.registerSvc("wedBoxIdEditAction", 1, new Action<IWedletActionCtx>("editId")
	.setLabel("Éditer l'identifiant")
	.setGroup("edit")
	.setIcon("/@skin@/edit/wed/box/editId.svg")
	.setVisible((ctx: IWedletActionCtx) => !ctx.wedlet.isVirtual() && isEditableWedlet(ctx.wedlet) && ctx.wedlet.editMode === EWedletEditMode.write)
	.setExecute(async (ctx: IWedletActionCtx) => {
		const docHolder = ctx.wedlet.wedMgr.docHolder;
		const boxId = ctx.focusedElt as BoxId;
		const newId = await POPUP.showDialog<string>(<div><ShadowJsx skin="standard-dialog" skinOver="webzone:panel">
			<style>{/* language=CSS */ `
		  :host {
			  min-width: 20em;
			  width: 20em;
		  }

		  #body {
			  display: flex;
			  flex-direction: column;
			  min-height: 0;
			  min-width: 0;
			  padding: .5em;
		  }

		  #msg {
			  padding: .5em 0;
		  }

		  label {
			  display: flex;
			  flex-direction: column;
			  min-height: 0;
			  min-width: 0;
		  }
			.idWidget{
				display: flex;
			}
			#input{
				border: 1px solid var(--border-color);
				border-radius: 0.2em;
				flex: 1 1 auto;
			}
			#input:invalid{
				border-color: var(--error-color);
			}
			#input:valid{
				border-color: var(--valid-color);
			}
			span.validity{
				font-size: 1.2em;
			}
			#input:invalid + span::after {
				content: "✖";
				color: var(--error-color);
				padding-left: 5px;
			}

			#input:valid + span::after {
				content: "✓";
				color: var(--valid-color);
				padding-left: 5px;
			}

			`}</style>
			<div id="body">
				<MsgLabel id="msg" î={{
					label: "Attention, changer d'identifiant aura pour effet de casser les liens établis vers ce fragment.",
					level: "warning", icon: "/@skin@/commons/widgets/popupable/warning.svg"
				} as OMsgLabelInit}/>
				<label>
					<div>Nouvel identifiant</div>
					<div class="idWidget">
						<input id="input" value={boxId.fragmentId} pattern="[a-zA-Z_]\w*" minlength="1" required/><span class="validity"/>
					</div>
					<em>Doit uniquement contenir des caractères alpha-numériques et ne peut pas commencer par un chiffre.</em>
				</label>
			</div>
			<div id="footer">
				<Button î={{label: "Changer", uiContext: "dialog"} as OButtonInit} onclick={function (this: Button) {
					if (!(DOMSH.findDocumentOrShadowRoot(this).getElementById('input') as HTMLInputElement).validity.valid) return;
					POPUP.findPopupableParent(this).close((DOMSH.findDocumentOrShadowRoot(this).getElementById('input') as HTMLInputElement).value);
				}
				}/>
				<Button î={{label: "Annuler", uiContext: "dialog"} as OButtonInit} onclick={function (this: Button) {
					POPUP.findPopupableParent(this).close(null);
				}
				}/>
			</div>
		</ShadowJsx></div>, ctx.focusedElt, {titleBar: "Éditer cet identifiant"}).onNextClose();
		const xa = ctx.wedlet.wedAnchor;
		if (newId && newId !== boxId.fragmentId) docHolder.newBatch({start: xa}).setAttr(xa, newId).doBatch();
	})
);
REG.reg.addSvcToList("actions:wed:box-id", "editId", 1, 'wedBoxIdEditAction');

REG.reg.addToList("actions:wed:box-id", "scComment", 2, null);
REG.reg.addToList("actions:wed:box-id", "delete", 2, null);
//Pas de fcts de clipboard.
REG.reg.addToList("actions:wed:box-id", "copy", 2, null);
REG.reg.addToList("actions:wed:box-id", "copySpecial", 2, null);
REG.reg.addToList("actions:wed:box-id", "cut", 2, null);
REG.reg.addToList("actions:wed:box-id", "paste", 2, null);
REG.reg.addToList("actions:wed:box-id", "pasteSpecial", 2, null);


/**
 * xml:id d'un élément quelconque
 */
export class BoxEltId extends BoxId {
	protected onFragmentIdChanged() {
		this.setAttribute("title", "Identifiant : " + this.fragmentId);
	}
}

AgEltBoxSelection(BoxEltId, {selMode: 'box', actionsLists: ['actions:wed:box-eltid', 'actions:wed:box-id'], groupOrder: '* edit'});

REG.reg.registerSkin("box-eltid", 1, /* language=CSS */ `
	:host {
		tab-index: 0;
		z-index: 2;
		cursor: default;
		user-select: none;
		color: var(--fade-color);
		font-style: italic;
		/* border: 1px solid var(--border-color); */
		border-radius: 0.75rem;
		/* height: 1.5em; */
		/* width: 1.5em; */
		align-self: start;
		text-align: center;
		padding-inline: 3px;
		font-size: .8rem;
		/* margin: 0.5em 0.5em -2em;*/
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}
`);

window.customElements.define("box-eltid", BoxEltId);
